import { supabase } from '../../supabase.js';
import { validateAndGetTenantUuid } from '../../utils/tenantUtils.js';
import { ServiceError } from '../../utils/errorHandler.js';

class AppStoreService {
  constructor() {
    this.tableName = 'apps';
  }

  /**
   * Search and discover apps in the store
   */
  async searchApps(searchParams = {}, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      let query = supabase
        .from(this.tableName)
        .select(`
          *,
          app_developers!inner(
            developer_name,
            company_name,
            verification_status
          )
        `)
        .eq('app_status', 'active');

      // Apply search filters
      if (searchParams.query) {
        query = query.or(`app_name.ilike.%${searchParams.query}%,app_description.ilike.%${searchParams.query}%`);
      }

      if (searchParams.category) {
        query = query.eq('app_category', searchParams.category);
      }

      if (searchParams.subcategory) {
        query = query.eq('app_subcategory', searchParams.subcategory);
      }

      if (searchParams.grade_level) {
        query = query.contains('grade_levels', [searchParams.grade_level]);
      }

      if (searchParams.price_range) {
        query = query.gte('subscription_price', searchParams.price_range.min)
                    .lte('subscription_price', searchParams.price_range.max);
      }

      if (searchParams.rating) {
        query = query.gte('rating', searchParams.rating);
      }

      // Apply sorting
      const sortBy = searchParams.sortBy || 'rating';
      const sortOrder = searchParams.sortOrder || 'desc';
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      if (searchParams.page && searchParams.limit) {
        const from = (searchParams.page - 1) * searchParams.limit;
        const to = from + searchParams.limit - 1;
        query = query.range(from, to);
      }

      const { data, error } = await query;

      if (error) {
        throw new ServiceError('APP_SEARCH_FAILED', 'Failed to search apps', error);
      }

      return {
        success: true,
        data: data || [],
        message: 'Apps retrieved successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('APP_SEARCH_FAILED', 'Failed to search apps', error);
    }
  }

  /**
   * Get app details by ID
   */
  async getAppById(appId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.tableName)
        .select(`
          *,
          app_developers!inner(
            developer_name,
            company_name,
            verification_status,
            revenue_share_percentage
          ),
          app_user_reviews(
            rating,
            review_title,
            review_content,
            review_date,
            users!inner(email)
          )
        `)
        .eq('id', appId)
        .eq('app_status', 'active')
        .single();

      if (error) {
        throw new ServiceError('APP_NOT_FOUND', 'App not found', error);
      }

      return {
        success: true,
        data,
        message: 'App details retrieved successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('APP_FETCH_FAILED', 'Failed to fetch app details', error);
    }
  }

  /**
   * Get apps by category
   */
  async getAppsByCategory(category, tenant_id, limit = 20) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.tableName)
        .select(`
          *,
          app_developers!inner(
            developer_name,
            company_name,
            verification_status
          )
        `)
        .eq('app_category', category)
        .eq('app_status', 'active')
        .order('rating', { ascending: false })
        .limit(limit);

      if (error) {
        throw new ServiceError('APP_CATEGORY_FETCH_FAILED', 'Failed to fetch apps by category', error);
      }

      return {
        success: true,
        data: data || [],
        message: 'Apps by category retrieved successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('APP_CATEGORY_FETCH_FAILED', 'Failed to fetch apps by category', error);
    }
  }

  /**
   * Get trending apps
   */
  async getTrendingApps(tenant_id, limit = 10) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.tableName)
        .select(`
          *,
          app_developers!inner(
            developer_name,
            company_name,
            verification_status
          )
        `)
        .eq('app_status', 'active')
        .order('total_downloads', { ascending: false })
        .order('rating', { ascending: false })
        .limit(limit);

      if (error) {
        throw new ServiceError('TRENDING_APPS_FETCH_FAILED', 'Failed to fetch trending apps', error);
      }

      return {
        success: true,
        data: data || [],
        message: 'Trending apps retrieved successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('TRENDING_APPS_FETCH_FAILED', 'Failed to fetch trending apps', error);
    }
  }

  /**
   * Get recommended apps for user
   */
  async getRecommendedApps(userId, tenant_id, limit = 10) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      // Get user's installed apps to understand preferences
      const { data: userApps, error: userAppsError } = await supabase
        .from('app_installations')
        .select('app_id')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (userAppsError) {
        throw new ServiceError('USER_APPS_FETCH_FAILED', 'Failed to fetch user apps', userAppsError);
      }

      // Get categories from user's installed apps
      const userCategories = [];
      if (userApps && userApps.length > 0) {
        const appIds = userApps.map(app => app.app_id);
        const { data: appsData } = await supabase
          .from(this.tableName)
          .select('app_category')
          .in('id', appIds);
        
        userCategories.push(...(appsData?.map(app => app.app_category) || []));
      }

      // Get recommended apps based on user preferences
      let query = supabase
        .from(this.tableName)
        .select(`
          *,
          app_developers!inner(
            developer_name,
            company_name,
            verification_status
          )
        `)
        .eq('app_status', 'active')
        .not('id', 'in', `(${userApps?.map(app => app.app_id).join(',') || '00000000-0000-0000-0000-000000000000'})`)
        .order('rating', { ascending: false })
        .limit(limit);

      // If user has preferences, prioritize those categories
      if (userCategories.length > 0) {
        query = query.in('app_category', userCategories);
      }

      const { data, error } = await query;

      if (error) {
        throw new ServiceError('RECOMMENDED_APPS_FETCH_FAILED', 'Failed to fetch recommended apps', error);
      }

      return {
        success: true,
        data: data || [],
        message: 'Recommended apps retrieved successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('RECOMMENDED_APPS_FETCH_FAILED', 'Failed to fetch recommended apps', error);
    }
  }

  /**
   * Get app categories
   */
  async getAppCategories(tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.tableName)
        .select('app_category, app_subcategory')
        .eq('app_status', 'active');

      if (error) {
        throw new ServiceError('CATEGORIES_FETCH_FAILED', 'Failed to fetch app categories', error);
      }

      // Process categories and subcategories
      const categories = {};
      data?.forEach(app => {
        if (!categories[app.app_category]) {
          categories[app.app_category] = new Set();
        }
        if (app.app_subcategory) {
          categories[app.app_category].add(app.app_subcategory);
        }
      });

      // Convert to array format
      const categoriesArray = Object.entries(categories).map(([category, subcategories]) => ({
        category,
        subcategories: Array.from(subcategories)
      }));

      return {
        success: true,
        data: categoriesArray,
        message: 'App categories retrieved successfully'
      };
    } catch (error) {
      throw error instanceof ServiceError ? error : new ServiceError('CATEGORIES_FETCH_FAILED', 'Failed to fetch app categories', error);
    }
  }
}

export const appStoreService = new AppStoreService();
